import React from 'react'
import { Link, useParams, Navigate } from 'react-router-dom'
import { Phone, AlertTriangle, ChevronRight, MapPin } from 'lucide-react'
import SEOHead from '@/components/SEO/SEOHead'
import PageHeader from '@/components/Common/PageHeader'
import { findTopic } from '@/data/species'
import { neighborhoods } from '@/data/neighborhoods'
import { contactInfo } from '@/data/contact'
import { toTelHref, toWhatsAppHref } from '@/utils/phone'
import {
  buildGraph,
  breadcrumbListData,
  procedureGuideSchema,
  faqData
} from '@/data/structured-data'

interface SpeciesTopicPageProps {
  speciesKey: 'kedi' | 'kopek'
}

const SpeciesTopicPage: React.FC<SpeciesTopicPageProps> = ({ speciesKey }) => {
  const { topic: topicSlug } = useParams()
  const found = findTopic(speciesKey, topicSlug)

  // Bilinmeyen konu: türün ana sayfasına yönlendirilir.
  if (!found) return <Navigate to={`/${speciesKey}`} replace />

  const { species: sp, topic } = found
  const url = `/${sp.key}/${topic.slug}`
  const telHref = toTelHref(contactInfo.phone)
  const waHref = toWhatsAppHref(contactInfo.whatsapp)

  // Aynı türün diğer konuları — sayfa içi gezinme ve iç bağlantı için.
  const siblings = sp.topics.filter(t => t.slug !== topic.slug)

  return (
    <>
      <SEOHead
        title={topic.metaTitle}
        description={topic.description}
        keywords={topic.keywords.join(', ')}
        canonicalUrl={url}
        ogType="article"
        ogImage={sp.image}
        structuredData={buildGraph(
          procedureGuideSchema({
            name: topic.title,
            description: topic.description,
            url,
            procedureType: 'Veteriner hekimlik uygulaması'
          }),
          faqData(topic.faqs),
          breadcrumbListData([
            { name: 'Ana Sayfa', url: '/' },
            { name: sp.heroTitle, url: `/${sp.key}` },
            { name: topic.title, url }
          ])
        )}
      />

      <PageHeader title={topic.title} subtitle={topic.description} />

      <section className="section-padding">
        <div className="container-custom grid grid-cols-1 lg:grid-cols-3 gap-10">
          <article className="lg:col-span-2">
            <p className="text-lg text-gray-700 leading-relaxed mb-8 pb-8 border-b border-gray-200">
              {topic.intro}
            </p>

            <div className="space-y-10">
              {topic.sections.map(sec => (
                <div key={sec.heading}>
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">{sec.heading}</h2>

                  {sec.paragraphs?.map((p, i) => (
                    <p key={i} className="text-gray-700 leading-relaxed mb-3">{p}</p>
                  ))}

                  {sec.bullets && (
                    <ul className="space-y-2 mt-3">
                      {sec.bullets.map((b, i) => (
                        <li key={i} className="flex items-start gap-2 text-gray-700 leading-relaxed">
                          <ChevronRight className="w-4 h-4 text-primary-500 mt-1 shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {sec.table && (
                    <div className="mt-4 overflow-x-auto rounded-xl border border-gray-200">
                      <table className="w-full text-sm">
                        {sec.table.caption && (
                          <caption className="text-left text-xs text-gray-500 px-4 py-2 bg-gray-50 border-b">
                            {sec.table.caption}
                          </caption>
                        )}
                        <thead className="bg-gray-50 text-gray-700">
                          <tr>
                            {sec.table.columns.map(c => (
                              <th key={c} className="text-left font-semibold px-4 py-3">{c}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {sec.table.rows.map((row, ri) => (
                            <tr key={ri} className="hover:bg-primary-50/40 transition-colors">
                              {row.map((cell, ci) => (
                                <td
                                  key={ci}
                                  className={`px-4 py-3 align-top ${ci === 0 ? 'font-medium text-gray-900 whitespace-nowrap' : 'text-gray-700'}`}
                                >
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* SSS — schema ile aynı içerik, kullanıcıya da görünür */}
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-5">Sık sorulan sorular</h2>
              <div className="space-y-3">
                {topic.faqs.map(f => (
                  <details
                    key={f.question}
                    className="group rounded-xl border border-gray-200 bg-white overflow-hidden"
                  >
                    <summary className="cursor-pointer list-none px-5 py-4 font-semibold text-gray-900 flex items-center justify-between gap-4 hover:bg-gray-50">
                      {f.question}
                      <ChevronRight className="w-4 h-4 text-gray-400 shrink-0 group-open:rotate-90 transition-transform" />
                    </summary>
                    <div className="px-5 pb-4 text-gray-700 leading-relaxed">{f.answer}</div>
                  </details>
                ))}
              </div>
            </div>

            <div className="mt-10 p-5 rounded-xl bg-gray-50 border border-gray-200 text-sm text-gray-600 leading-relaxed">
              Bu sayfadaki bilgiler genel bilgilendirme amaçlıdır ve muayene yerine geçmez.
              {' '}{sp.labelPlural}in yaşı, kilosu ve sağlık geçmişi uygulanacak programı değiştirir.
              Kesin plan için kliniğimizde muayene olmanızı öneririz.
            </div>
          </article>

          <aside className="space-y-6">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <h3 className="font-bold text-gray-900 mb-2">Randevu alın</h3>
              <p className="text-sm text-gray-600 mb-4">
                Şanlıurfa Karaköprü’deki kliniğimizde {topic.title.toLowerCase()} ve diğer tüm
                {' '}{sp.label.toLowerCase()} sağlığı hizmetleri için randevu alabilirsiniz.
              </p>
              <a href={telHref} className="btn-primary w-full inline-flex items-center justify-center gap-2 mb-2">
                <Phone className="w-4 h-4" /> {contactInfo.phone}
              </a>
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary w-full inline-flex items-center justify-center"
              >
                WhatsApp’tan yazın
              </a>
            </div>

            {siblings.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
                <h3 className="font-bold text-gray-900 mb-3">{sp.heroTitle} — diğer konular</h3>
                <ul className="space-y-2">
                  {siblings.map(t => (
                    <li key={t.slug}>
                      <Link
                        to={`/${sp.key}/${t.slug}`}
                        className="text-sm text-primary-700 hover:underline inline-flex items-center gap-1"
                      >
                        <ChevronRight className="w-3.5 h-3.5" /> {t.title}
                      </Link>
                    </li>
                  ))}
                  <li>
                    <Link
                      to={`/hastaliklari/${sp.key === 'kedi' ? 'kedi' : 'kopek'}`}
                      className="text-sm text-primary-700 hover:underline inline-flex items-center gap-1"
                    >
                      <ChevronRight className="w-3.5 h-3.5" /> {sp.label} hastalıkları
                    </Link>
                  </li>
                </ul>
              </div>
            )}

            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-4 h-4 text-primary-600" />
                <h3 className="font-bold text-gray-900">Hizmet bölgemiz</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                Karaköprü’nün {neighborhoods.length} mahallesine hizmet veriyoruz.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {neighborhoods.slice(0, 6).map(n => (
                  <Link
                    key={n.slug}
                    to={`/karakopru-veteriner/${n.slug}`}
                    className="px-2.5 py-1 rounded-full border border-gray-200 text-xs text-gray-600 hover:border-primary-300 hover:text-primary-700 transition-colors"
                  >
                    {n.name}
                  </Link>
                ))}
              </div>
              <Link
                to="/karakopru-veteriner"
                className="text-sm font-semibold text-primary-700 hover:underline mt-3 inline-block"
              >
                Tüm mahalleler →
              </Link>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <h3 className="font-bold text-red-900">Acil durum</h3>
              </div>
              <p className="text-sm text-red-800 leading-relaxed">
                Nefes darlığı, havale, zehirlenme ve travmada beklemeyin. 7/24 acil hizmet veriyoruz.
              </p>
              <Link to="/acil-veteriner" className="text-sm font-semibold text-red-700 hover:underline mt-3 inline-block">
                Acil veteriner →
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </>
  )
}

export default SpeciesTopicPage
